export interface PasswordRule {
  id: string
  labelKey: string
  test: (value: string) => boolean
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    labelKey: 'password.ruleLength',
    test: (v) => v.length >= 8,
  },
  {
    id: 'upper',
    labelKey: 'password.ruleUpper',
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: 'lower',
    labelKey: 'password.ruleLower',
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: 'digit',
    labelKey: 'password.ruleDigit',
    test: (v) => /\d/.test(v),
  },
  {
    id: 'special',
    labelKey: 'password.ruleSpecial',
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
]

export type StrengthLevel = 'empty' | 'weak' | 'fair' | 'good' | 'strong'

export interface StrengthResult {
  score: number
  max: number
  level: StrengthLevel
  passed: Record<string, boolean>
}

/**
 * Entropy-ish score from regex rule hits + length bonus (0–100).
 */
export function scorePassword(value: string): StrengthResult {
  if (!value) {
    return {
      score: 0,
      max: 100,
      level: 'empty',
      passed: Object.fromEntries(PASSWORD_RULES.map((r) => [r.id, false])),
    }
  }

  const passed = Object.fromEntries(
    PASSWORD_RULES.map((r) => [r.id, r.test(value)]),
  )
  const hits = Object.values(passed).filter(Boolean).length
  const lengthBonus = Math.min(25, Math.max(0, value.length - 8) * 3)
  const score = Math.min(100, hits * 15 + lengthBonus)

  let level: StrengthLevel = 'weak'
  if (score >= 85) level = 'strong'
  else if (score >= 65) level = 'good'
  else if (score >= 40) level = 'fair'

  return { score, max: 100, level, passed }
}
